import type { ObservationDto } from "@repo/data-store";
import { Badge } from "@repo/ui/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import type { ReactNode } from "react";

/** Symptom category code → human source label. */
const SOURCE_LABELS: Record<string, string> = {
  survey: "Patient-reported",
  exam: "Clinician-observed",
};

function pickDate(s: ObservationDto): string | undefined {
  return s.effectiveDateTime ?? s.effectivePeriod?.start ?? s.effectiveInstant;
}

/** Sort by effective date desc; missing date sinks to the bottom. */
export function sortByEffectiveDesc(items: ObservationDto[]): ObservationDto[] {
  return [...items].sort((a, b) => {
    const ta = pickDate(a) ? Date.parse(pickDate(a) as string) : Number.NEGATIVE_INFINITY;
    const tb = pickDate(b) ? Date.parse(pickDate(b) as string) : Number.NEGATIVE_INFINITY;
    return tb - ta;
  });
}

function sourceLabel(s: ObservationDto): string | undefined {
  const code = s.category?.map((c) => c.code).find((c) => c && SOURCE_LABELS[c]);
  return code ? SOURCE_LABELS[code] : undefined;
}

function Root({ children }: { children: ReactNode }) {
  return <Card size="sm">{children}</Card>;
}

function Header({ title = "Symptoms", action }: { title?: string; action?: ReactNode }) {
  return (
    <CardHeader>
      <div className="flex w-full items-center justify-between">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {action}
      </div>
    </CardHeader>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <CardContent>
      <div className="space-y-4">{children}</div>
    </CardContent>
  );
}

function Empty({ children = "No symptoms recorded." }: { children?: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

function Item({ symptom }: { symptom: ObservationDto }) {
  const title = symptom.code?.display ?? symptom.code?.text ?? symptom.code?.code ?? "Symptom";
  const date = pickDate(symptom);
  const source = sourceLabel(symptom);
  const severity = symptom.valueCodeableConcept?.display ?? symptom.valueCodeableConcept?.text;
  const note = symptom.notes?.[0]?.text;

  return (
    <div className="block min-w-0 border-l-2 border-border py-0.5 pl-2.5">
      <div className="text-sm font-medium leading-snug">{title}</div>
      {date && <div className="mt-0.5 text-xs text-muted-foreground">{new Date(date).toLocaleDateString()}</div>}
      {note && <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{note}</div>}
      {(source || severity) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {source && (
            <Badge variant="outline" className="px-1.5 py-0 text-xs">
              {source}
            </Badge>
          )}
          {severity && (
            <Badge variant="secondary" className="px-1.5 py-0 text-xs">
              {severity}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export const SymptomList = Object.assign(Root, {
  Header,
  Body,
  Empty,
  Item,
});
